<template>
  <div>
    <div
      class="my-6 flex relative rounded-xl border border-gray-96 focus-within:border-blue-medium bg-gray-96 focus-within:bg-white transition-colors duration-300 ease-in-out"
    >
      <UTextarea
        data-test="message_place_holder"
        ref="promptTextarea"
        v-model="promptMessage"
        :placeholder="isRecording && !isMdScreen ? '' : $t('placeholder.defaultMessage')"
        class="py-4 pl-6 flex-1"
        :max-rows="5"
        :rows="promptTextareaRows"
        :disabled="isThinking"
        @focus="handleFocus"
        @keydown.enter.prevent="handleSubmit"
      />

      <button
        v-if="isAnswering"
        type="button"
        class="btn btn-secondary px-4"
        @click="handleStop"
      >
        <IconsStop class="w-8 h-8" />
      </button>

      <button
        v-else-if="isPromptMessageEmpty && !isRecording"
        v-tooltip="recordingButtonTooltip"
        type="button"
        class="btn btn-secondary px-4"
        @click="handleSoundInput"
        :disabled="isRecordingError || isThinking"
      >
        <IconsAudioMicrophone class="w-8 h-8" />
      </button>

      <div
        v-else-if="isRecording"
        class="absolute top-3 left-3 right-3 md:static flex md:items-center"
      >
        <button
          v-tooltip="recordingButtonTooltip"
          type="button"
          class="btn btn-destructive mx-4 pl-3 gap-1 rounded-full text-sm animate-pulse w-max flex-1 justify-between hover:animate-none"
          @click="handleSoundInput"
        >
          <IconsStop class="opacity-0 w-8 h-8 md:hidden" />
          <span class="flex-1">{{ $t('buttons.audio.recordingYourVoice') }}</span>
          <IconsStop class="w-8 h-8" />
        </button>
      </div>
      <button
        v-else
        v-tooltip="{ content: t('controls.sendMessage'), theme: 'tooltip-light' }"
        type="button"
        class="btn btn-secondary px-4"
        @click="handleSubmit"
        :disabled="isPromptMessageEmpty || isThinking"
      >
        <IconsSend class="w-8 h-8" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import UTextarea from '@/components/ui/UTextarea.vue'
import { useConversationStore } from '@/stores/conversationStore'
import { storeToRefs } from 'pinia'
import { breakpointsTailwind, useBreakpoints, useDebounceFn, useResizeObserver } from '@vueuse/core'
import { useRecording } from '@/composables/useRecording'
import { useSpeechToText } from '@/composables/useSpeechToText'
import Clarity from '@microsoft/clarity'

const { t } = useI18n()
const conversationStore = useConversationStore()
const { handleSendMessage, handleStop } = conversationStore
const { promptRef, promptMessage, isThinking, isAnswering } = storeToRefs(conversationStore)
const { startRecording, stopRecording, isRecording } = useRecording()
const { audioAvailableHandler, startConversion, stopConversion } = useSpeechToText()

const promptTextareaRef = useTemplateRef('promptTextarea')
watch(promptTextareaRef, () => {
  if (promptTextareaRef.value !== null) {
    promptRef.value = promptTextareaRef.value.$refs.textarea as HTMLTextAreaElement | null
  }
})

const isPromptMessageEmpty = computed(() => promptMessage.value.trim() === '')

const handleSubmit = (e: Event) => {
  const event = e as KeyboardEvent
  event.preventDefault()
  if (event.shiftKey && event.key === 'Enter') return
  if (isPromptMessageEmpty.value) return
  handleSendMessage()
  Clarity.event('sentMessage')
  promptMessage.value = ''
}

const recordingButtonTooltip = computed(() => ({
  content: isRecording.value ? t('controls.audio.stopRecording') : t('controls.audio.startRecording'),
  theme: 'tooltip-light',
  disabled: isRecordingError.value || isThinking.value
}))

const breakpoints = useBreakpoints(breakpointsTailwind)
const computeIsMdScreen = () => {
  return breakpoints.isGreaterOrEqual('md')
}
const isMdScreen = ref(computeIsMdScreen())
const resizeHandler = useDebounceFn(() => {
  isMdScreen.value = computeIsMdScreen()
}, 300)
useResizeObserver(document.body, resizeHandler)

const promptTextareaRows = computed(() => {
  if (isMdScreen.value) {
    return 2
  }

  return 3
})

const isRecordingError = ref(false)

const handleStopRecording = async () => {
  await stopRecording()
  stopConversion()
}

const textAvailableHandler = (text: string) => {
  promptMessage.value += text
}

const handleSoundInput = async () => {
  isRecordingError.value = false
  Clarity.event('UsedVoiceInput')
  if (isRecording.value) {
    await handleStopRecording()
  } else {
    startConversion(textAvailableHandler)
    const startedRecording = await startRecording(audioAvailableHandler)
    isRecordingError.value = !startedRecording
  }
}

const handleFocus = async () => {
  await handleStopRecording()
}

watch(isThinking, async newVal => {
  if (newVal === false) {
    promptMessage.value = ''
  }
})
</script>
